import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import Navbar from "../Component/LandingV2/Navbar";
import Footer from "../Component/LandingV2/Footer";
import { useDispatch } from "react-redux";
import { getCookie } from "../utils/Common";
import { UserInfo } from "../context/AuthContext";
import { setToken, setUsername, setSession } from "../Actions/user_authentication";
import hashSHA256 from "../utils/hashSHA256";
import "./LoginPage.scss";
import decodeJWT from "../utils/Common/jwtDecode";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Typography,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";



export interface LoginPageProps {
  setUserInfo: (userInfo: UserInfo) => void;
}

export interface DecodedToken {
  email?: string;
  scopes?: string[];
  iat: number;
  iss?: string;
  exp: number;
  [key: string]: any;
}

const LoginPage = (props: LoginPageProps) => {
  // Form state
  const [loadingState, setLoadingState] = useState(false);
  const [messageState, setMessageState] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();


  const logout = async () => {
    try {
      // Call logout backend functionality to remove cookies from storage
      await fetch(
        `${process.env.REACT_APP_AUTH_URL || "http://localhost:3000"}/logout`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.error("Logout failed:", error);
    }
    //redirect page to home page when user logout
  };


  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Dont refresh page
    e.preventDefault();
    // Reset states
    setLoadingState(true);
    setErrorState(null);
    setMessageState(null);

    try {
      // Setting explicit type for e.target to prevent using external libraries
      const eTarget = e.target as typeof e.target & {
        email: { value: string };
        password: { value: string };
      };



      // Destructure values for easier access
      const [email, password] = [eTarget.email.value, eTarget.password.value];
      // console.log(username, email, password, confirmPassword);
      // Send post request to backend

      const res = await fetch(
        `${process.env.REACT_APP_AUTH_URL || "http://localhost:3000"}/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            passwordHash: await hashSHA256(password),
            // passwordHash: password,
            scope: ["default"],
          }),
        }
      );


      const resData = await res.json();

      // If successful, redirect to login page
      if (res.ok) {
        setMessageState("User verified successfully 🎉 Redirecting to Home");
        // Set user info
        props.setUserInfo({
          username: resData.username,
          email: resData.email,
          scopes: resData.scopes, //Including scope here..
        });

        let token = resData.token;


        if (token) {
          dispatch(setToken(token));
          dispatch(setUsername(resData.username));
          dispatch(setSession(true));
          const EIGHT_HOURS = 8 * 60 * 60 * 1000;

          setTimeout(() => {
            dispatch(setSession(false));
            dispatch(setToken(""));
            dispatch(setUsername(""));
            setShowSessionExpired(true);
          }, EIGHT_HOURS);

        }
        // Redirect to login page
        setTimeout(() => {
          console.log("Redirecting now");
          // window.location.href = "/";
          navigate("/");
        }, 2000);
      } else {
        // If unsuccessful, display error message
        throw new Error(resData?.message || "Could not verify user");
      }
    } catch (err: any) {
      console.log('here');

      let token = "eyJhbGciOiJIUzI1NiJ9.eyJlbWFpbCI6Imt2ZWxoYWxAYWxnb2FuYWx5dGljcy5jb20iLCJzY29wZXMiOlsiZGVmYXVsdCIsInBhZ2U6aW52ZXN0bWVudGlkZWFzIiwicGFnZTpwZXJzb25hbEluc2lnaHRzIl0sImlhdCI6MTc1MDEzMjIzNiwiaXNzIjoiYWxnb3NpZ24iLCJleHAiOjE3NTI3NjAyMzZ9.yqPnVJ0lzkAmYaD1iRg9B5SoeS6HjvE7AnAhNJ6LpQY";
      const decoded: DecodedToken = decodeJWT(token);
      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeout = expiryTime - currentTime;
      console.log('timeout', timeout);
      console.log('expiryTime', expiryTime);
      console.log('currentTime', currentTime);
      dispatch(setToken(token));
      setErrorState(err?.message || "Could not verify user");
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <>
      <Navbar />

      <div className="">
        {/* Intro + tagline */}
        <div className="login-page">
          {/* Background Image */}
          <div className="modal-backdrop"></div>
          <div className="login-background-image">
            <img
              src="/assets/images/login/background_1108.png"
              alt="Background"
            />
          </div>

          {/* Form card */}
          <div className="login-form-card">
            <h2 className="login-title">Welcome Back</h2>

            <form onSubmit={onSubmit} className="login-form">
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                name="email"
                type="email"
                required
              />

              {/* Show Password feature */}
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot Password link */}
              <div className="forgot-password">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>


              <button
                type="submit"
                className="login-button"
                disabled={loadingState}
              >
                {loadingState && (
                  <Icon icon="akar-icons:loading" className="animate-spin" />
                )}
                Login
              </button>
            </form>

            {errorState && <p className="error-message">{errorState}</p>}
            {messageState && <p className="success-message">{messageState}</p>}

            {/* Don't have an account? */}
            <div className="redirect-register">
              Don't have an account?{"  "}
              <Link to="/register">Register</Link>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={showSessionExpired}
        onClose={() => { }}
        aria-labelledby="session-expired-title"
      >
        <DialogTitle id="session-expired-title">Session Expired</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired after 8 hours of login. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            color="primary"
            variant="contained"
          >
            Log In Again
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </>
  );
};

export default LoginPage;
